// app/src/lib/procurement/vendorCompare.js — "compare a new vendor's list".
//
// Orchestrates the end-to-end flow for the Buy-tab feature: upload a prospective
// supplier's price sheet (PDF/photo) → existing S3→OCR pipeline parses it into
// product lines → send those lines plus the items we actively buy to the
// vendor-compare Lambda (Claude does the fuzzy cross-vendor matching) → fold the
// matched pairs into a gap/price report (pure logic in vendorCompareMath.js).
//
// The owner's item master lives client-side, so "what we buy" is gathered here
// and sent up with the vendor lines — the server never persists either list.

import { getConfig } from '../config.js';
import { getAccessToken } from '../auth/cognitoClient.js';
import { uploadReceiptImage, pollOcrResult } from '../ocr/uploader.js';
import { db } from '../db.js';
import { requireUserId, normalize } from './util.js';
import { listActive } from './items.js';
import { buildReport } from './vendorCompareMath.js';

const PRICE_LOOKBACK_MONTHS = 6;
const MAX_ITEMS = 400; // matches the server's MAX_OUR_ITEMS cap

/**
 * The items we ACTIVELY buy. For OCR-first books the Item Master (db.items) and
 * price book are empty — the real purchase history lives in the line items of
 * scanned invoices (same reason PriceCheck reads scanned line items, not the
 * catalog). So we build the baseline from the last 6 months of invoice line
 * items: dedupe by product description, keep the most recently purchased
 * occurrence and its unit price. Falls back to the Item Master for books that
 * actually use the catalog.
 *
 * @returns {Promise<Array<{ itemId:string, name:string, category?:string, packSize?:string, uom?:string, lastPriceUsd?:number }>>}
 */
export async function gatherPurchasedItems() {
  const userId = await requireUserId();
  const cutoff = new Date(); cutoff.setUTCMonth(cutoff.getUTCMonth() - PRICE_LOOKBACK_MONTHS);
  const cutoffIso = cutoff.toISOString().slice(0, 10);

  const invoices = await db.invoices
    .filter((i) => i && i.userId === userId && (i.issuedDate || '') >= cutoffIso && i.status !== 'DISPUTED')
    .toArray();

  if (invoices.length) {
    const invDate = new Map(invoices.map((i) => [i.invoiceId, i.issuedDate || '']));
    const ids = new Set(invoices.map((i) => i.invoiceId));
    const lineItems = await db.lineItems
      .filter((li) => li && li.userId === userId && ids.has(li.invoiceId))
      .toArray();

    // Dedupe by normalized description, keeping the most recently purchased
    // occurrence (and its price).
    const byKey = new Map();
    for (const li of lineItems) {
      const name = (li.description || '').trim();
      if (!name) continue;
      const key = normalize(name);
      if (!key) continue;
      const date = invDate.get(li.invoiceId) || '';
      const prev = byKey.get(key);
      if (!prev || date > prev.date) {
        byKey.set(key, {
          name,
          date,
          uom: typeof li.uom === 'string' ? li.uom : undefined,
          lastPriceUsd: Number.isFinite(li.unitPriceCents) ? li.unitPriceCents / 100 : undefined,
        });
      }
    }

    if (byKey.size) {
      // Most-recently-purchased first, so if we exceed the cap we keep the freshest.
      return Array.from(byKey.values())
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
        .slice(0, MAX_ITEMS)
        .map((v) => ({ itemId: `li:${normalize(v.name)}`, name: v.name, uom: v.uom, lastPriceUsd: v.lastPriceUsd }));
    }
  }

  // Fallback: catalog Item Master (books that use it).
  const items = await listActive();
  return items
    .filter((it) => it && it.name)
    .slice(0, MAX_ITEMS)
    .map((it) => ({ itemId: it.itemId, name: it.name, category: it.category, packSize: it.packSize, uom: it.defaultUom }));
}

/**
 * Ask the backend to match the vendor's lines against our items (Claude).
 * @param {{ ourItems:Array, vendorLines:Array }} payload
 * @returns {Promise<{ matches:Array }>}
 */
export async function matchVendorList({ ourItems, vendorLines }) {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('vendorCompare: not signed in');
  const res = await fetch(`${cfg.apiBaseUrl}/v1/procurement/vendor-compare`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify({ ourItems, vendorLines }),
  });
  if (!res.ok) {
    let info = null;
    try { info = await res.json(); } catch { /* non-JSON */ }
    const e = new Error((info && info.error) || `vendor-compare ${res.status}`);
    e.status = res.status;
    if (info && info.code) e.code = info.code;
    throw e;
  }
  return res.json();
}

/**
 * Full flow. `onStage` is called with 'uploading' | 'reading' | 'matching' so
 * the screen can show progress. The Blob should already be compressed/right-sized
 * by the caller (same as the receipt scan path).
 *
 * @param {Blob} blob
 * @param {string} mimeType - "image/jpeg" | "image/png" | "image/webp" | "application/pdf"
 * @param {{ onStage?: (stage:string)=>void }} [opts]
 * @returns {Promise<{ vendorName?:string, report:{matches:Array, missing:Array}, vendorLineCount:number, ourItemCount:number }>}
 */
export async function compareNewVendorList(blob, mimeType, { onStage } = {}) {
  const stage = (s) => { try { onStage && onStage(s); } catch { /* ignore */ } };
  const log = (...a) => { try { console.log('[vendorCompare]', ...a); } catch { /* ignore */ } };

  // Gather what we buy FIRST. If there's nothing to compare against, bail BEFORE
  // spending an OCR scan on the (potentially many-page) vendor sheet — the
  // comparison baseline is this device's recent scanned purchases.
  stage('gathering');
  const ourItems = await gatherPurchasedItems();
  log('purchased items gathered:', ourItems.length);
  if (!ourItems.length) {
    const e = new Error('No recent purchases on this device to compare against. Scan a few of your supplier invoices first so Kanjo knows what you buy, then try again.');
    e.code = 'NO_ITEMS';
    throw e;
  }

  stage('uploading');
  const { receiptId: listId } = await uploadReceiptImage(blob, mimeType, { kind: 'vendorlist' });
  log('uploaded vendor sheet, id =', listId);

  stage('reading');
  const ocr = await pollOcrResult(listId, { kind: 'vendorlist', timeoutMs: 120_000 });
  const vendorLines = Array.isArray(ocr?.lines) ? ocr.lines : [];
  log('OCR returned', vendorLines.length, 'vendor lines; vendorName =', ocr?.vendorName);
  if (!vendorLines.length) {
    const e = new Error("We couldn't read any products from that file. Try a clearer photo or the original PDF.");
    e.code = 'NO_VENDOR_LINES';
    throw e;
  }

  stage('matching');
  log('calling match API:', ourItems.length, 'items ×', vendorLines.length, 'lines');
  const { matches } = await matchVendorList({ ourItems, vendorLines });
  log('match returned', Array.isArray(matches) ? matches.length : 0, 'matches');
  const report = buildReport(ourItems, matches);
  log('report:', report.matches.length, 'carried,', report.missing.length, 'missing');
  return {
    vendorName: ocr?.vendorName,
    report,
    vendorLineCount: vendorLines.length,
    ourItemCount: ourItems.length,
  };
}
